import {
  Component,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  ElementRef,
  ViewChild,
  HostListener,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export interface CalendarDay {
  dayNumber: number;
  date: Date;
  iso: string;
  isCurrentMonth: boolean;
  isToday: boolean;
  isSelected: boolean;
  isDisabled: boolean;
}

@Component({
  selector: 'app-custom-datepicker',
  standalone: true,
  imports: [CommonModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomDatepickerComponent),
      multi: true,
    },
  ],
  template: `
    <div class="abaq-datepicker-container">
      <div
        class="abaq-datepicker-trigger"
        [class.is-open]="isOpen"
        [class.is-disabled]="disabled"
        [class.is-invalid]="invalid"
      >
        <span class="material-symbols-outlined trigger-icon">calendar_today</span>

        <input
          #inputEl
          type="text"
          [id]="id"
          class="abaq-datepicker-input"
          [value]="rawInputValue"
          (input)="onRawInput($event)"
          (focus)="onInputFocus()"
          (blur)="onInputBlur()"
          (keydown)="onInputKeyDown($event)"
          [placeholder]="placeholder || 'DD/MM/AAAA'"
          [disabled]="disabled"
          maxlength="10"
          autocomplete="off"
          role="combobox"
          aria-haspopup="dialog"
          [attr.aria-expanded]="isOpen"
        />

        @if (value && !disabled) {
          <button
            type="button"
            tabindex="-1"
            class="icon-action-btn clear-btn"
            title="Borrar fecha"
            (click)="clearValue($event)"
            aria-label="Borrar fecha"
          >
            <span class="material-symbols-outlined" style="font-size: 18px;">close</span>
          </button>
        }

        <button
          type="button"
          tabindex="-1"
          class="icon-action-btn calendar-toggle-btn"
          (click)="toggleOpen($event)"
          [disabled]="disabled"
          title="Abrir calendario"
          aria-label="Abrir calendario"
        >
          <span class="material-symbols-outlined abaq-datepicker-chevron" [class.rotated]="isOpen">
            calendar_month
          </span>
        </button>
      </div>

      @if (isOpen) {
        <div class="abaq-datepicker-dropdown" (click)="$event.stopPropagation()">
          <!-- HEADER DE NAVEGACIÓN -->
          <div class="calendar-header">
            <button
              type="button"
              class="nav-btn"
              (click)="navigatePrev($event)"
              title="Anterior"
            >
              <span class="material-symbols-outlined">chevron_left</span>
            </button>

            <div class="header-titles">
              <button
                type="button"
                class="view-mode-btn"
                [class.active]="viewMode === 'months'"
                (click)="toggleViewMode('months', $event)"
              >
                {{ monthNames[viewMonth] }}
              </button>
              <button
                type="button"
                class="view-mode-btn"
                [class.active]="viewMode === 'years'"
                (click)="toggleViewMode('years', $event)"
              >
                {{ viewYear }}
              </button>
            </div>

            <button
              type="button"
              class="nav-btn"
              (click)="navigateNext($event)"
              title="Siguiente"
            >
              <span class="material-symbols-outlined">chevron_right</span>
            </button>
          </div>

          <!-- MODO: DÍAS (DEFAULT) -->
          @if (viewMode === 'days') {
            <div class="calendar-weekdays">
              @for (dayName of weekDayNames; track dayName) {
                <div class="weekday-cell">{{ dayName }}</div>
              }
            </div>

            <div class="calendar-grid">
              @for (cell of calendarDays; track cell.iso) {
                <button
                  type="button"
                  class="day-cell"
                  [class.other-month]="!cell.isCurrentMonth"
                  [class.is-today]="cell.isToday"
                  [class.is-selected]="cell.isSelected"
                  [disabled]="cell.isDisabled"
                  (click)="onSelectDay(cell, $event)"
                >
                  {{ cell.dayNumber }}
                </button>
              }
            </div>
          }

          <!-- MODO: MESES -->
          @if (viewMode === 'months') {
            <div class="months-grid">
              @for (m of shortMonthNames; track $index) {
                <button
                  type="button"
                  class="month-cell"
                  [class.is-selected]="$index === viewMonth"
                  (click)="onSelectMonth($index, $event)"
                >
                  {{ m }}
                </button>
              }
            </div>
          }

          <!-- MODO: AÑOS -->
          @if (viewMode === 'years') {
            <div class="years-grid">
              @for (y of visibleYears; track y) {
                <button
                  type="button"
                  class="year-cell"
                  [class.is-selected]="y === viewYear"
                  (click)="onSelectYear(y, $event)"
                >
                  {{ y }}
                </button>
              }
            </div>
          }

          <!-- FOOTER DE ACCIONES RÁPIDAS -->
          <div class="calendar-footer">
            <button
              type="button"
              class="footer-btn today-btn"
              (click)="onSetToday($event)"
            >
              Hoy
            </button>

            @if (value) {
              <button
                type="button"
                class="footer-btn clear-text-btn"
                (click)="clearValue($event)"
              >
                Limpiar
              </button>
            }

            <button
              type="button"
              class="footer-btn close-btn"
              (click)="isOpen = false"
            >
              Cerrar
            </button>
          </div>
        </div>
      }
    </div>
  `,
  styles: [
    `
      .abaq-datepicker-container {
        position: relative;
        width: 100%;
        display: block;
        box-sizing: border-box;
      }

      .abaq-datepicker-trigger {
        width: 100%;
        min-height: 44px;
        border-radius: 10px;
        border: 1px solid #dce2e8;
        background: #ffffff;
        font-family: inherit;
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 0 8px 0 14px;
        transition: all 0.2s ease;
        box-sizing: border-box;
        overflow: hidden;
      }

      .abaq-datepicker-trigger:focus-within,
      .abaq-datepicker-trigger.is-open {
        border-color: #24366f;
        box-shadow: 0 0 0 3px rgba(36, 54, 111, 0.14);
      }

      .abaq-datepicker-trigger.is-invalid {
        border-color: #dc2626 !important;
        background-color: #fff5f5 !important;
        box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.14) !important;
      }

      .abaq-datepicker-trigger.is-disabled {
        background-color: #f1f5f9;
        cursor: not-allowed;
        border-color: #e2e8f0;
      }

      .trigger-icon {
        font-size: 19px;
        color: #24366f;
        flex-shrink: 0;
      }

      .abaq-datepicker-input {
        flex: 1;
        width: 100%;
        height: 100%;
        min-height: 42px;
        padding: 10px 0;
        border: none;
        outline: none;
        background: transparent;
        font-family: inherit;
        font-size: 14px;
        color: #1e293b;
        font-weight: 500;
        box-sizing: border-box;
      }

      .abaq-datepicker-input::placeholder {
        color: #94a3b8;
        font-weight: 400;
      }

      .abaq-datepicker-input:disabled {
        color: #94a3b8;
        cursor: not-allowed;
      }

      .icon-action-btn {
        background: transparent;
        border: none;
        padding: 6px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        color: #64748b;
        outline: none;
        border-radius: 6px;
        transition: all 0.15s ease;
      }

      .icon-action-btn:hover {
        background: rgba(36, 54, 111, 0.08);
        color: #24366f;
      }

      .clear-btn:hover {
        background: #fee2e2;
        color: #dc2626;
      }

      .abaq-datepicker-chevron {
        font-size: 20px;
        color: #24366f;
        transition: transform 0.2s ease;
      }

      .abaq-datepicker-chevron.rotated {
        transform: rotate(180deg);
        color: #5ca83a;
      }

      /* DROPDOWN POPUP */
      .abaq-datepicker-dropdown {
        position: absolute;
        top: calc(100% + 6px);
        left: 0;
        min-width: 290px;
        max-width: 320px;
        background: #ffffff;
        border: 1px solid #e2e8f0;
        border-radius: 14px;
        box-shadow: 0 12px 30px -4px rgba(15, 23, 42, 0.16), 0 4px 12px -2px rgba(15, 23, 42, 0.08);
        padding: 14px;
        z-index: 1100;
        box-sizing: border-box;
        animation: abaqCalendarFade 0.16s ease-out;
      }

      @keyframes abaqCalendarFade {
        from {
          opacity: 0;
          transform: translateY(-6px) scale(0.98);
        }
        to {
          opacity: 1;
          transform: translateY(0) scale(1);
        }
      }

      /* HEADER */
      .calendar-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 12px;
      }

      .header-titles {
        display: flex;
        align-items: center;
        gap: 6px;
      }

      .view-mode-btn {
        background: none;
        border: none;
        font-size: 14.5px;
        font-weight: 700;
        color: #1e293b;
        padding: 4px 8px;
        border-radius: 6px;
        cursor: pointer;
        transition: all 0.15s ease;
      }

      .view-mode-btn:hover {
        background: rgba(36, 54, 111, 0.07);
        color: #24366f;
      }

      .view-mode-btn.active {
        background: #24366f;
        color: #ffffff;
      }

      .nav-btn {
        background: #f8fafc;
        border: 1px solid #e2e8f0;
        border-radius: 8px;
        width: 32px;
        height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        color: #475569;
        transition: all 0.15s ease;
      }

      .nav-btn:hover {
        background: #e2e8f0;
        color: #0f172a;
      }

      .nav-btn .material-symbols-outlined {
        font-size: 18px;
      }

      /* WEEKDAYS */
      .calendar-weekdays {
        display: grid;
        grid-template-columns: repeat(7, 1fr);
        gap: 2px;
        margin-bottom: 6px;
        text-align: center;
      }

      .weekday-cell {
        font-size: 11px;
        font-weight: 700;
        color: #64748b;
        text-transform: uppercase;
        padding: 4px 0;
      }

      /* GRID DE DÍAS */
      .calendar-grid {
        display: grid;
        grid-template-columns: repeat(7, 1fr);
        gap: 3px;
      }

      .day-cell {
        aspect-ratio: 1;
        width: 100%;
        max-width: 38px;
        margin: 0 auto;
        border-radius: 8px;
        border: 1px solid transparent;
        background: none;
        font-size: 13px;
        font-weight: 500;
        color: #1e293b;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: all 0.15s ease;
        padding: 0;
      }

      .day-cell:hover:not(:disabled):not(.is-selected) {
        background: rgba(36, 54, 111, 0.08);
        color: #24366f;
      }

      .day-cell.other-month {
        color: #94a3b8;
        opacity: 0.6;
      }

      .day-cell.is-today {
        border-color: #24366f;
        font-weight: 700;
        color: #24366f;
      }

      .day-cell.is-selected {
        background: #24366f !important;
        color: #ffffff !important;
        font-weight: 700;
        box-shadow: 0 4px 8px rgba(36, 54, 111, 0.3);
      }

      .day-cell:disabled {
        color: #cbd5e1;
        cursor: not-allowed;
        background: none;
      }

      /* GRID DE MESES */
      .months-grid {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 8px;
        padding: 8px 0;
      }

      .month-cell {
        padding: 10px 4px;
        border-radius: 8px;
        border: 1px solid #e2e8f0;
        background: #f8fafc;
        font-size: 13px;
        font-weight: 600;
        color: #1e293b;
        cursor: pointer;
        transition: all 0.15s ease;
      }

      .month-cell:hover:not(.is-selected) {
        background: rgba(36, 54, 111, 0.08);
        color: #24366f;
        border-color: #24366f;
      }

      .month-cell.is-selected {
        background: #24366f;
        color: #ffffff;
        border-color: #24366f;
      }

      /* GRID DE AÑOS */
      .years-grid {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 8px;
        max-height: 200px;
        overflow-y: auto;
        padding: 4px 2px;
      }

      .year-cell {
        padding: 8px 4px;
        border-radius: 8px;
        border: 1px solid #e2e8f0;
        background: #f8fafc;
        font-size: 13px;
        font-weight: 600;
        color: #1e293b;
        cursor: pointer;
        transition: all 0.15s ease;
      }

      .year-cell:hover:not(.is-selected) {
        background: rgba(36, 54, 111, 0.08);
        color: #24366f;
        border-color: #24366f;
      }

      .year-cell.is-selected {
        background: #24366f;
        color: #ffffff;
        border-color: #24366f;
      }

      /* FOOTER */
      .calendar-footer {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-top: 12px;
        padding-top: 10px;
        border-top: 1px solid #e2e8f0;
      }

      .footer-btn {
        background: none;
        border: none;
        font-size: 12.5px;
        font-weight: 600;
        cursor: pointer;
        padding: 4px 8px;
        border-radius: 6px;
        transition: all 0.15s ease;
      }

      .today-btn {
        color: #059669;
      }

      .today-btn:hover {
        background: #ecfdf5;
      }

      .clear-text-btn {
        color: #dc2626;
      }

      .clear-text-btn:hover {
        background: #fee2e2;
      }

      .close-btn {
        color: #64748b;
      }

      .close-btn:hover {
        background: #f1f5f9;
        color: #1e293b;
      }
    `,
  ],
})
export class CustomDatepickerComponent implements ControlValueAccessor, OnInit {
  @Input() id = '';
  @Input() placeholder = 'DD/MM/AAAA';
  @Input() disabled = false;
  @Input() invalid = false;
  @Input() min = '';
  @Input() max = '';

  @Output() valueChange = new EventEmitter<string>();

  @ViewChild('inputEl') inputRef?: ElementRef<HTMLInputElement>;

  isOpen = false;
  viewMode: 'days' | 'months' | 'years' = 'days';

  value: string = ''; // ISO format: 'YYYY-MM-DD'
  rawInputValue = '';

  viewYear: number = new Date().getFullYear();
  viewMonth: number = new Date().getMonth(); // 0 to 11
  yearRangeStart: number = new Date().getFullYear() - 6;

  monthNames = [
    'Enero',
    'Febrero',
    'Marzo',
    'Abril',
    'Mayo',
    'Junio',
    'Julio',
    'Agosto',
    'Septiembre',
    'Octubre',
    'Noviembre',
    'Diciembre',
  ];

  shortMonthNames = [
    'Ene',
    'Feb',
    'Mar',
    'Abr',
    'May',
    'Jun',
    'Jul',
    'Ago',
    'Sep',
    'Oct',
    'Nov',
    'Dic',
  ];

  weekDayNames = ['Do', 'Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sá'];

  onChange: any = () => {};
  onTouched: any = () => {};

  constructor(private elementRef: ElementRef) {}

  ngOnInit(): void {
    if (this.value) {
      this.syncViewWithDate(this.value);
      this.rawInputValue = this.isoToFormatted(this.value);
    }
  }

  get visibleYears(): number[] {
    const years: number[] = [];
    const start = this.yearRangeStart;
    for (let i = 0; i < 15; i++) {
      years.push(start + i);
    }
    return years;
  }

  get calendarDays(): CalendarDay[] {
    const days: CalendarDay[] = [];
    const year = this.viewYear;
    const month = this.viewMonth;

    const firstDayIndex = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const daysInPrevMonth = new Date(year, month, 0).getDate();

    const todayStr = this.formatIso(new Date());

    // Días del mes previo
    for (let i = firstDayIndex - 1; i >= 0; i--) {
      const dayNum = daysInPrevMonth - i;
      const prevDate = new Date(year, month - 1, dayNum);
      const iso = this.formatIso(prevDate);
      days.push({
        dayNumber: dayNum,
        date: prevDate,
        iso,
        isCurrentMonth: false,
        isToday: iso === todayStr,
        isSelected: iso === this.value,
        isDisabled: this.isDateDisabled(iso),
      });
    }

    // Días del mes actual
    for (let i = 1; i <= daysInMonth; i++) {
      const curDate = new Date(year, month, i);
      const iso = this.formatIso(curDate);
      days.push({
        dayNumber: i,
        date: curDate,
        iso,
        isCurrentMonth: true,
        isToday: iso === todayStr,
        isSelected: iso === this.value,
        isDisabled: this.isDateDisabled(iso),
      });
    }

    // Días del siguiente mes para completar 35 o 42 celdas
    const remaining = (7 - (days.length % 7)) % 7;
    for (let i = 1; i <= remaining; i++) {
      const nextDate = new Date(year, month + 1, i);
      const iso = this.formatIso(nextDate);
      days.push({
        dayNumber: i,
        date: nextDate,
        iso,
        isCurrentMonth: false,
        isToday: iso === todayStr,
        isSelected: iso === this.value,
        isDisabled: this.isDateDisabled(iso),
      });
    }

    return days;
  }

  private isDateDisabled(iso: string): boolean {
    if (this.min && iso < this.min) return true;
    if (this.max && iso > this.max) return true;
    return false;
  }

  private formatIso(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  private isoToFormatted(iso: string): string {
    if (!iso) return '';
    const parts = iso.split('-');
    if (parts.length !== 3) return iso;
    const y = parts[0];
    const m = parts[1];
    const d = parts[2];
    return `${d}/${m}/${y}`;
  }

  private syncViewWithDate(val: string): void {
    if (!val) return;
    const parts = val.split('-');
    if (parts.length === 3) {
      const y = parseInt(parts[0], 10);
      const m = parseInt(parts[1], 10) - 1;
      if (!isNaN(y) && !isNaN(m)) {
        this.viewYear = y;
        this.viewMonth = m;
        this.yearRangeStart = y - 7;
      }
    }
  }

  onRawInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    const digits = target.value.replace(/\D/g, '').slice(0, 8);

    let formatted = '';
    if (digits.length <= 2) {
      formatted = digits;
    } else if (digits.length <= 4) {
      formatted = `${digits.slice(0, 2)}/${digits.slice(2)}`;
    } else {
      formatted = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}`;
    }

    this.rawInputValue = formatted;
    target.value = formatted;

    if (digits.length === 8) {
      const parsed = this.parseDayMonthYear(digits);
      if (parsed) {
        const iso = `${parsed.y}-${String(parsed.m).padStart(2, '0')}-${String(parsed.d).padStart(2, '0')}`;
        if (!this.isDateDisabled(iso)) {
          this.value = iso;
          this.syncViewWithDate(iso);
          this.onChange(this.value);
          this.valueChange.emit(this.value);
        }
      }
    } else if (digits.length === 0) {
      this.value = '';
      this.onChange('');
      this.valueChange.emit('');
    }
  }

  private parseDayMonthYear(digits: string): { d: number; m: number; y: number } | null {
    const p1 = parseInt(digits.slice(0, 2), 10);
    const p2 = parseInt(digits.slice(2, 4), 10);
    const y = parseInt(digits.slice(4, 8), 10);

    let d = p1;
    let m = p2;

    if (p1 > 12 && p2 <= 12) {
      d = p1;
      m = p2;
    } else if (p2 > 12 && p1 <= 12) {
      d = p2;
      m = p1;
    } else {
      d = p1;
      m = p2;
    }

    if (this.isValidDate(d, m, y)) {
      return { d, m, y };
    }
    return null;
  }

  private isValidDate(d: number, m: number, y: number): boolean {
    if (y < 1900 || y > 2100) return false;
    if (m < 1 || m > 12) return false;
    const daysInMonth = new Date(y, m, 0).getDate();
    return d >= 1 && d <= daysInMonth;
  }

  onInputFocus(): void {
    if (this.disabled) return;
    this.onTouched();
  }

  onInputBlur(): void {
    setTimeout(() => {
      if (this.value) {
        this.rawInputValue = this.isoToFormatted(this.value);
      } else if (this.rawInputValue.replace(/\D/g, '').length < 8) {
        this.rawInputValue = '';
      }
    }, 200);
  }

  onInputKeyDown(event: KeyboardEvent): void {
    if (this.disabled) return;

    if (event.key === 'ArrowDown' || event.key === 'F4') {
      event.preventDefault();
      this.openCalendar();
    } else if (event.key === 'Escape') {
      this.isOpen = false;
    } else if (event.key === 'Tab') {
      this.isOpen = false;
    }
  }

  toggleOpen(event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();
    if (this.disabled) return;
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.viewMode = 'days';
      if (this.value) {
        this.syncViewWithDate(this.value);
      } else {
        const now = new Date();
        this.viewYear = now.getFullYear();
        this.viewMonth = now.getMonth();
        this.yearRangeStart = this.viewYear - 7;
      }
      this.onTouched();
    }
  }

  private openCalendar(): void {
    this.isOpen = true;
    this.viewMode = 'days';
    if (this.value) {
      this.syncViewWithDate(this.value);
    } else {
      const now = new Date();
      this.viewYear = now.getFullYear();
      this.viewMonth = now.getMonth();
      this.yearRangeStart = this.viewYear - 7;
    }
    this.onTouched();
  }

  toggleViewMode(mode: 'months' | 'years', event: MouseEvent): void {
    event.stopPropagation();
    if (this.viewMode === mode) {
      this.viewMode = 'days';
    } else {
      this.viewMode = mode;
      if (mode === 'years') {
        this.yearRangeStart = this.viewYear - 7;
      }
    }
  }

  navigatePrev(event: MouseEvent): void {
    event.stopPropagation();
    if (this.viewMode === 'days') {
      if (this.viewMonth === 0) {
        this.viewMonth = 11;
        this.viewYear--;
      } else {
        this.viewMonth--;
      }
    } else if (this.viewMode === 'months') {
      this.viewYear--;
    } else if (this.viewMode === 'years') {
      this.yearRangeStart -= 15;
    }
  }

  navigateNext(event: MouseEvent): void {
    event.stopPropagation();
    if (this.viewMode === 'days') {
      if (this.viewMonth === 11) {
        this.viewMonth = 0;
        this.viewYear++;
      } else {
        this.viewMonth++;
      }
    } else if (this.viewMode === 'months') {
      this.viewYear++;
    } else if (this.viewMode === 'years') {
      this.yearRangeStart += 15;
    }
  }

  onSelectDay(cell: CalendarDay, event: MouseEvent): void {
    event.stopPropagation();
    if (cell.isDisabled) return;
    this.value = cell.iso;
    this.rawInputValue = this.isoToFormatted(cell.iso);
    this.syncViewWithDate(cell.iso);
    this.onChange(this.value);
    this.valueChange.emit(this.value);
    this.isOpen = false;
  }

  onSelectMonth(monthIndex: number, event: MouseEvent): void {
    event.stopPropagation();
    this.viewMonth = monthIndex;
    this.viewMode = 'days';
  }

  onSelectYear(year: number, event: MouseEvent): void {
    event.stopPropagation();
    this.viewYear = year;
    this.viewMode = 'days';
  }

  onSetToday(event: MouseEvent): void {
    event.stopPropagation();
    const todayStr = this.formatIso(new Date());
    if (this.isDateDisabled(todayStr)) return;
    this.value = todayStr;
    this.rawInputValue = this.isoToFormatted(todayStr);
    this.syncViewWithDate(todayStr);
    this.onChange(this.value);
    this.valueChange.emit(this.value);
    this.isOpen = false;
  }

  clearValue(event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();
    this.value = '';
    this.rawInputValue = '';
    this.onChange('');
    this.valueChange.emit('');
    this.inputRef?.nativeElement.focus();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.isOpen = false;
    }
  }

  @HostListener('keydown.escape')
  onEscape(): void {
    this.isOpen = false;
  }

  writeValue(val: any): void {
    if (val instanceof Date) {
      this.value = this.formatIso(val);
    } else {
      this.value = val || '';
    }
    if (this.value) {
      this.syncViewWithDate(this.value);
      this.rawInputValue = this.isoToFormatted(this.value);
    } else {
      this.rawInputValue = '';
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
